import {modify, selectAs} from 'partial.lenses';
import {anyPass, omit, pipe, replace, test, toLower} from 'ramda';
import {messageHandler} from '@sinclair-digital/stl-common/src/sinclair/sinclair.uiTools';
import {fetchJsonV1} from '@sinclair-digital/v2-content-service/lib/fetch';
import {
    GeneralSettings,
    defaultGeneralSettings,
    generalSettingsShape,
    hasGeneralSettingsErrors,
    validate as validateGeneral,
    serializeGeneral,
    parseGeneral,
} from './GeneralSettings/General';
import {
    ThemeSettings,
    defaultThemeSettings,
    themeSettingsShape,
    validate as validateTheme,
    hasThemeSettingsErrors,
    parseThemeSettings,
    serializeThemeSettings,
} from './ThemeSettings/Theme';
import {
    defaultIntegrationSettings,
    integrationSettingsShape,
    serializeIntegration,
    Integration,
    parseIntegrationSettings,
    validate as validateIntegration,
    hasIntegrationSettingsErrors,
} from './IntegrationSettings/Integration';
import {packageTypeShape, PackageType, parsePackageTypes, serializePackageTypes} from './PackageTypes/PackageType';
import {
    defaultSource as defaultSources,
    sourcesShape,
    Sources,
    parseSources,
    serializeSources,
} from './Sources/Sources';
import {
    Social,
    defaultSocialSettings,
    parseSocialSettings,
    validate as validateSocial,
    hasSocialSettingsErrors,
    serializeSocialSettings as serializeSocial,
} from './SocialSettings/Social';
import {PropertySiteV1} from '@sinclair-digital/stl-common/src/models/Property';

export interface Property {
    uuid?: string;
    generalSettings: GeneralSettings;
    themeSettings: ThemeSettings;
    integrationSettings: Integration;
    socialSettings: Social;
    packageTypes: PackageType[];
    sources: Sources;
    // bucket for any server properties we don't care about
    rest: {
        [k: string]: any;
    };
}

export type TabName =
    | 'generalSettings'
    | 'themeSettings'
    | 'integrationSettings'
    | 'socialSettings'
    | 'sources'
    | 'packageTypes';

export const parseProperty = (data: PropertySiteV1): Property => {
    // This has to match all root fields that are handled by parsePropertySettings
    // TODO remove this maintenance problem
    const rest = omit(
        [
            'backgroundColorOne',
            'backgroundColorTwo',
            'brandColorOne',
            'brandColorTwo',
            'callLetters',
            'channel',
            'city',
            'designatedMarketArea',
            'dfpAdUnitId',
            'dfpNetworkId',
            'excludedSources',
            'faviconImageUrl',
            'footerLogoImageUrl',
            'footerText',
            'galleryLogoImageUrl',
            'general',
            'googleAnalyticsId',
            'headerLogoImageUrl',
            'imageUrl',
            'includedSources',
            'integration',
            'name',
            'network',
            'packageTypes',
            'social',
            'sources',
            'stagingUrl',
            'state',
            'textColorFour',
            'textColorOne',
            'textColorThree',
            'textColorTwo',
            'timezone',
            'url',
            'uuid',
            'weatherImageOneUrl',
            'weatherImageTwoUrl',
            'zipcode',
        ],
        data,
    );
    return {
        uuid: data.uuid,
        generalSettings: parseGeneral(data),
        themeSettings: parseThemeSettings(data),
        integrationSettings: parseIntegrationSettings(data),
        packageTypes: parsePackageTypes(data.packageTypes),
        sources: parseSources(data),
        socialSettings: parseSocialSettings(data),
        rest,
    };
};

export const propertySettingsShape = {
    ...generalSettingsShape,
    ...themeSettingsShape,
    integrationSettings: integrationSettingsShape,
    ...sourcesShape,
    ...packageTypeShape,
};

export const createEmptyPropertySettings = (): Property => ({
    generalSettings: defaultGeneralSettings,
    themeSettings: defaultThemeSettings,
    integrationSettings: defaultIntegrationSettings,
    packageTypes: [],
    sources: defaultSources,
    socialSettings: defaultSocialSettings,
    rest: {},
});

export const adjustStagingUrl = (val: string) => {
    const transform1 = replace(/(\.\w*)$/g, '');
    const transform2 = replace(/(\/:)/g, ''); // does this do anything??
    const urlString = pipe(
        toLower,
        transform1,
        transform2,
    )(val);

    return `${urlString}.${window.location.hostname}`;
};

export const adjustProductionUrl = (str: string) => {
    let fixedString = pipe(
        toLower,
        replace(/((http|https):\/\/)|(www\.)|(\.com)|\s/g, ''),
    )(str);

    if (!test(/\./, fixedString)) {
        fixedString += '.com';
    }
    return fixedString;
};

export const fetchProperty = (uuid: string): Promise<Property> =>
    fetchJsonV1(`/api/rest/facade/site/${uuid}`).then((data: any) => {
        const raw: PropertySiteV1 = data.data[0];
        return parseProperty(raw);
    });

export const serializeProperty = (property: Property): PropertySiteV1 => {
    const rest = property.rest;
    const social = serializeSocial(property.socialSettings);
    const themeSettings = serializeThemeSettings(property.themeSettings);
    const packageTypes = serializePackageTypes(property.packageTypes);
    const sources = serializeSources(property.sources);
    const general = serializeGeneral(property.generalSettings);
    const integration = serializeIntegration(property.integrationSettings);
    return {
        ...general,
        ...themeSettings,
        packageTypes,
        ...sources,
        ...integration,
        social,
        ...rest,
    };
};

export const saveProperty = pipe(
    serializeProperty,
    ({uuid, ...data}: PropertySiteV1) => {
        (uuid
            ? fetchJsonV1(`/api/rest/facade/site/${uuid}`, {
                method: 'PUT',
                body: JSON.stringify({data: {...data, uuid}}),
            })
            : fetchJsonV1(`/api/rest/facade/site`, {
                method: 'POST',
                body: JSON.stringify({data: {...data, uuid: null}}),
            })
        )
            .then((response: any) => {
                const raw: PropertySiteV1 = response.data[0];
                window.location.assign(`#property/${raw.uuid}`);
            })
            .catch(error => {
                if (test(/zip/, error)) {
                    messageHandler('error', 'Invalid Zipcode');
                } else {
                    messageHandler('error', error);
                }
            });
    },
);

export const validate = pipe(
    modify('generalSettings', validateGeneral),
    modify('themeSettings', validateTheme),
    modify('integrationSettings', validateIntegration),
    modify('socialSettings', validateSocial),
);

export const hasErrors = anyPass([
    selectAs<Property, boolean>(hasGeneralSettingsErrors, 'generalSettings'),
    selectAs<Property, boolean>(hasThemeSettingsErrors, 'themeSettings'),
    selectAs<Property, boolean>(hasIntegrationSettingsErrors, 'integrationSettings'),
    selectAs<Property, boolean>(hasSocialSettingsErrors, 'socialSettings'),
]);
